import { useId, useMemo, useState } from 'react';

import { AlertTriangleIcon, CheckIcon, ChevronDownIcon, CircleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SpinnerIcon } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

import { LiveInstructionOption, SelectOption, useMatchFieldsModal } from './useMatchFieldsModal';
import { MatchFieldsModalProps, VariableDefinition } from '../../types/column-mapping';

/**
 * Searchable single-select picker — used for every NBA variable that takes
 * one CSV column (everything except `multi: true` targets).
 */
const SingleSelectColumnPicker = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  dontMapLabel,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  options: SelectOption[];
  placeholder: string;
  disabled?: boolean;
  dontMapLabel: string;
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const listboxId = useId();

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-stroke bg-background px-3 py-2 text-sm',
            'focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          <span
            className={cn('truncate', selectedOption ? 'text-foreground' : 'text-foreground-muted')}
          >
            {selectedOption?.label || placeholder}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0" align="start" onWheel={(e) => e.stopPropagation()}>
        <Command>
          <CommandInput placeholder={t('campaigns.match_fields.search_variable')} />
          <CommandList id={listboxId}>
            <CommandEmpty>{t('campaigns.match_fields.no_variables_found')}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                key="__none__"
                value={dontMapLabel}
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <CheckIcon
                  className={cn('mr-2 size-4', value === null ? 'opacity-100' : 'opacity-0')}
                />
                <span className="text-foreground-muted italic">{dontMapLabel}</span>
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option.value ?? 'skip'}
                  value={option.label}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      'mr-2 size-4',
                      value === option.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Multi-select column picker — used for `multi: true` variables
 * (alternate_emails / alternate_phones) that can collect several CSV
 * columns into a single bucket.
 */
const MultiSelectColumnPicker = ({
  selected,
  onToggle,
  options,
  placeholder,
  disabled,
}: {
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
  options: SelectOption[];
  placeholder: string;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const listboxId = useId();

  const getSummaryLabel = () => {
    if (selected.length === 0) {
      return placeholder;
    }
    if (selected.length === 1) {
      return selected[0];
    }
    return t('campaigns.match_fields.n_columns_selected', { 0: selected.length });
  };
  const summary = getSummaryLabel();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-stroke bg-background px-3 py-2 text-sm',
            'focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          <span
            className={cn(
              'truncate',
              selected.length > 0 ? 'text-foreground' : 'text-foreground-muted'
            )}
          >
            {summary}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0" align="start" onWheel={(e) => e.stopPropagation()}>
        <Command>
          <CommandInput placeholder={t('campaigns.match_fields.search_variable')} />
          <CommandList id={listboxId}>
            <CommandEmpty>{t('campaigns.match_fields.no_variables_found')}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                if (!option.value) {
                  return null;
                }
                const isChecked = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => onToggle(option.value!, !isChecked)}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => onToggle(option.value!, checked === true)}
                      className="mr-2"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Bottom panel: select which subscriber-context values to mirror under
 * `context.live_instructions.*`. Two-column grid with a leading
 * "Select all" master checkbox. Hidden when there is nothing to offer
 * (e.g. while auto-suggest is still running and no mappings exist).
 */
const LiveInstructionsPanel = ({
  options,
  selected,
  onToggle,
  onToggleAll,
  disabled,
}: {
  options: LiveInstructionOption[];
  selected: Set<string>;
  onToggle: (path: string) => void;
  onToggleAll: (checked: boolean) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();

  const masterState = useMemo<boolean | 'indeterminate'>(() => {
    if (options.length === 0) {
      return false;
    }
    if (selected.size === 0) {
      return false;
    }
    if (selected.size === options.length) {
      return true;
    }
    return 'indeterminate';
  }, [options.length, selected.size]);

  if (options.length === 0) {
    return null;
  }

  return (
    <Accordion type="single" collapsible className="rounded-md border border-stroke">
      <AccordionItem value="live-instructions" className="border-b-0">
        <AccordionTrigger className="px-4 py-3 hover:no-underline focus-visible:border-transparent focus-visible:ring-0">
          <div className="flex flex-col gap-1 text-left">
            <h3 className="text-sm font-semibold text-foreground">
              {t('campaigns.match_fields.live_instructions_title')}
              {selected.size > 0 && (
                <span className="ml-2 rounded-full bg-bg-muted-50 px-2 py-0.5 text-xs font-medium text-foreground-muted">
                  {selected.size}
                </span>
              )}
            </h3>
            <p className="text-xs font-normal text-foreground-muted">
              {t('campaigns.match_fields.live_instructions_description')}
            </p>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4">
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Checkbox
                checked={masterState}
                disabled={disabled}
                onCheckedChange={(checked) => onToggleAll(checked === true)}
                aria-label={t('campaigns.match_fields.select_all')}
              />
              <span>{t('campaigns.match_fields.select_all')}</span>
            </label>

            <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              {options.map((option) => {
                const id = `li-${option.path}`;
                const isChecked = selected.has(option.path);
                return (
                  <label
                    key={option.path}
                    htmlFor={id}
                    className="flex items-center gap-2 text-sm text-foreground"
                  >
                    <Checkbox
                      id={id}
                      checked={isChecked}
                      disabled={disabled}
                      onCheckedChange={() => onToggle(option.path)}
                    />
                    <span className="truncate">
                      {option.label}
                      {option.slotLabel && (
                        <span className="ml-1 text-foreground-muted">[{option.slotLabel}]</span>
                      )}
                      {option.origin === 'csv' && (
                        <span className="ml-1 text-foreground-muted">
                          ({t('campaigns.match_fields.from_csv')})
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

/**
 * Modal for matching system (NBA) variables to CSV columns.
 *
 * **Reverse layout (MOV-16264):** rows are anchored on the NBA-required
 * variables; the dropdown picks which CSV column feeds each one. Sections
 * are still Contact details / Debt details (#N), with the debt slot count
 * auto-detected from CSV header patterns.
 *
 * **Copy to live_instructions (MOV-15472):** the bottom panel lets the user
 * pick which subscriber-context values should also be mirrored under
 * `context.live_instructions.*`, replacing the support team's manual
 * duplication.
 *
 * All logic lives in `useMatchFieldsModal`; this component just renders it.
 */
export const MatchFieldsModal = (props: MatchFieldsModalProps) => {
  const { t } = useTranslation();
  const {
    isSuggesting,
    debtSlotCount,
    variablesBySection,
    hasMultipleEmails,
    hasMultiplePhones,
    missingRequiredFields,
    handleVariableMappingChange,
    handleMultiVariableToggle,
    handleConfirm,
    csvOptionsForVariable,
    findHeadersForVariable,
    getPreviewValue,
    liveInstructionOptions,
    liveInstructionTargets,
    toggleLiveInstructionTarget,
    setAllLiveInstructionTargets,
  } = useMatchFieldsModal(props);

  const { open, onOpenChange } = props;
  // Suppress both banners while the auto-suggest call is in flight. During
  // that window every column reads as unmapped, so the missing-required and
  // duplicate-email/phone warnings would flash incorrectly and then vanish
  // once the LLM response lands. Wait for the analysis to settle first.
  const showPrimaryAlert = !isSuggesting && (hasMultipleEmails || hasMultiplePhones);
  const hasMissingRequired = !isSuggesting && missingRequiredFields.length > 0;

  const dontMapLabel = t('campaigns.match_fields.dont_map');

  const renderVariableRow = (variable: VariableDefinition, debtIndex: number) => {
    const mappedHeaders = findHeadersForVariable(variable.name, debtIndex);
    const csvOptions = csvOptionsForVariable(variable.name, debtIndex);
    const preview = mappedHeaders.length > 0 ? getPreviewValue(mappedHeaders[0]) : null;
    const rowKey = `${variable.name}-${debtIndex}`;
    const isRequired = missingRequiredFields.some(
      (v) => v.name === variable.name && v.debtIndex === debtIndex
    );
    // Highlight rows whose required field still has no CSV column matched, so
    // the unresolved requirement is obvious at a glance (suppressed while the
    // auto-suggest analysis is still settling to avoid a flash).
    const showRequiredWarning = !isSuggesting && isRequired;

    return (
      <div
        key={rowKey}
        className={cn(
          'flex items-center gap-3 rounded-md px-2 py-1',
          showRequiredWarning && 'bg-bg-warning-soft'
        )}
      >
        <div className="flex w-[220px] items-center gap-2">
          {showRequiredWarning && (
            <CircleAlertIcon className="size-4 shrink-0 text-warning" aria-hidden />
          )}
          <span
            className={cn(
              'truncate text-sm text-foreground',
              variable.required || isRequired ? 'font-semibold' : 'font-medium'
            )}
          >
            {variable.label || variable.name}
            {(variable.required || isRequired) && <span className="ml-1 text-destructive">*</span>}
            {variable.conditionalGroup && (
              <span className="ml-1 text-foreground-muted">({t('campaigns.match_fields.or')})</span>
            )}
          </span>
        </div>
        <div className="w-[180px] shrink-0 truncate text-sm text-foreground-muted">
          {preview ?? <span className="italic">{t('campaigns.match_fields.no_preview')}</span>}
        </div>
        <div className="flex-1">
          {isSuggesting ? (
            <div
              className="flex h-10 w-full items-center gap-2 rounded-md border border-stroke bg-bg-muted-50 px-3 py-2 text-sm text-foreground-muted"
              aria-busy="true"
              aria-label={t('campaigns.match_fields.analyzing')}
            >
              <SpinnerIcon className="size-4 shrink-0 text-accent" />
              <span className="truncate">{t('campaigns.match_fields.analyzing')}</span>
            </div>
          ) : variable.multi ? (
            <MultiSelectColumnPicker
              selected={mappedHeaders}
              onToggle={(header, checked) =>
                handleMultiVariableToggle(variable.name, header, checked)
              }
              options={csvOptions}
              placeholder={t('campaigns.match_fields.select_column')}
            />
          ) : (
            <SingleSelectColumnPicker
              value={mappedHeaders[0] ?? null}
              onChange={(header) => handleVariableMappingChange(variable.name, debtIndex, header)}
              options={csvOptions}
              placeholder={t('campaigns.match_fields.select_column')}
              dontMapLabel={dontMapLabel}
            />
          )}
        </div>
      </div>
    );
  };

  const renderSection = (
    sectionId: string,
    title: string,
    variables: VariableDefinition[],
    debtIndex: number
  ) => {
    if (variables.length === 0) {
      return null;
    }
    return (
      <div key={sectionId} className="flex flex-col gap-2">
        <div className="flex items-center justify-between border-b border-stroke pb-2">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        <div className="flex flex-col gap-1">
          {variables.map((variable) => renderVariableRow(variable, debtIndex))}
        </div>
      </div>
    );
  };

  const renderPrimaryAlertCard = (
    titleKey: string,
    bodyKey: string,
    cardKey: 'email' | 'phone'
  ) => (
    <div key={cardKey} className="flex items-start gap-3 rounded-md border border-warning p-3">
      <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-warning" />
      <div className="flex flex-col text-sm">
        <span className="font-semibold text-foreground">{t(titleKey)}</span>
        <span className="text-foreground-muted">{t(bodyKey)}</span>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[784px]">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-5">
          <DialogTitle className="text-2xl leading-none font-semibold tracking-[-0.6px]">
            {t('campaigns.match_fields.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-4">
          {hasMissingRequired && (
            <div className="flex items-start gap-3 rounded-md border border-destructive p-3">
              <CircleAlertIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
              <div className="flex flex-col gap-1 text-sm">
                <span className="font-semibold text-foreground">
                  {t('campaigns.match_fields.missing_required_title')}
                </span>
                <div className="flex flex-wrap gap-1">
                  {missingRequiredFields.map((field) => (
                    <span
                      key={`${field.name}-${field.debtIndex}`}
                      className="rounded-md bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
                    >
                      {field.label}
                      {field.slotLabel && ` (${field.slotLabel})`}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {showPrimaryAlert && (
            <div className="flex flex-col gap-2">
              {hasMultipleEmails &&
                renderPrimaryAlertCard(
                  'campaigns.match_fields.primary_email_alert_title',
                  'campaigns.match_fields.primary_email_alert_body',
                  'email'
                )}
              {hasMultiplePhones &&
                renderPrimaryAlertCard(
                  'campaigns.match_fields.primary_phone_alert_title',
                  'campaigns.match_fields.primary_phone_alert_body',
                  'phone'
                )}
            </div>
          )}

          {/* Column header row */}
          <div className="flex items-center gap-3 px-2 text-xs font-medium tracking-wide text-foreground-muted uppercase">
            <div className="w-[220px]">{t('campaigns.match_fields.system_field')}</div>
            <div className="w-[180px]">{t('campaigns.match_fields.preview_data')}</div>
            <div className="flex-1">{t('campaigns.match_fields.from_column')}</div>
          </div>

          <div className="scrollbar-hide flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-2">
            {renderSection(
              'contact',
              t('campaigns.match_fields.contact_details'),
              variablesBySection.contact ?? [],
              0
            )}

            {Array.from({ length: debtSlotCount }, (_, i) => {
              const sectionId = `debt-${i}`;
              const title =
                debtSlotCount === 1
                  ? t('campaigns.match_fields.debt_details')
                  : t('campaigns.match_fields.debt_details_n', { 0: i + 1 });
              return renderSection(sectionId, title, variablesBySection[sectionId] ?? [], i);
            })}

            <LiveInstructionsPanel
              options={liveInstructionOptions}
              selected={liveInstructionTargets}
              onToggle={toggleLiveInstructionTarget}
              onToggleAll={setAllLiveInstructionTargets}
              disabled={isSuggesting}
            />
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t px-6 pt-4 pb-6">
          <Button
            variant="ghost-neutral"
            className="min-w-20 text-foreground"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={isSuggesting} className="min-w-24">
            {t('common.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
